import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { generateFinalPDF } from "../../utils/api";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, message } from "antd";

const Generate = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [report, setReport] = useState(null);
	// Mirrors `report` so the unmount cleanup below can reach the latest value.
	const reportRef = useRef(null);

	useEffect(() => {
		const timer = setTimeout(() => setIsVisible(true), 100);
		return () => clearTimeout(timer);
	}, []);

	// Release the generated report when leaving the page.
	useEffect(() => {
		return () => {
			if (reportRef.current?.url) {
				window.URL.revokeObjectURL(reportRef.current.url);
			}
		};
	}, []);

	const handleMarketList = () => {
		const data = JSON.parse(localStorage.getItem("formValues"));
		if (data) {
			const { make, model, trim, city, modelYear } = data;
			navigate("/find_car?update=true", {
				state: {
					formInputValues: data,
					filterInput: {
						make,
						model,
						trim,
						city,
						year_min: modelYear,
						year_max: modelYear,
					},
				},
			});
		} else {
			message.warning(
				"Please start a new evaluation first to set vehicle details.",
			);
			navigate("/form_page");
		}
	};
	const handleSalvageList = () => {
		navigate("/find_salvageCar");
	};
	const handleForm = () => {
		const keysToRemove = [
			"compareCars",
			"formValues",
			"salvageCars",
			"marketCars",
			"filters",
			"compareSalvage",
			"marketRadius",
			"salvageRadius",
		];
		keysToRemove.forEach((k) => {
			localStorage.removeItem(k);
		});
		navigate("/form_page");
	};

	const buildFileName = (inputValues) => {
		const slug = [inputValues?.modelYear, inputValues?.make, inputValues?.model]
			.filter(Boolean)
			.join("-")
			.toLowerCase()
			.replace(/[^a-z0-9-]+/g, "-")
			.replace(/-+/g, "-")
			.replace(/^-|-$/g, "");
		return slug ? `conditions-report-${slug}.pdf` : "conditions-report.pdf";
	};

	const triggerDownload = (target) => {
		if (!target?.url) {
			return;
		}
		const link = document.createElement("a");
		link.href = target.url;
		link.download = target.filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleGenerate = async () => {
		const compareCars = JSON.parse(localStorage.getItem("marketCars"));
		const inputValues = JSON.parse(localStorage.getItem("formValues"));
		const salvageCars = JSON.parse(localStorage.getItem("salvageCars"));

		// Validate required data
		if (!Array.isArray(compareCars) || compareCars.length === 0) {
			message.error(
				"No market value cars selected. Please add market value cars first.",
			);
			return;
		}
		if (!Array.isArray(salvageCars) || salvageCars.length === 0) {
			message.error("No salvage cars selected. Please add salvage cars first.");
			return;
		}
		if (!inputValues) {
			message.error(
				"No vehicle information found. Please start a new evaluation.",
			);
			return;
		}

		setLoading(true);

		try {
			const comparePDF = await generateFinalPDF({
				marketvalue: {
					selected_car: compareCars,
					marketvalue: inputValues,
				},
				salvage_value: {
					salvageCars,
				},
			});

			// generateFinalPDF resolves with the error object instead of rejecting,
			// so a failed request arrives here looking like a success. Without this
			// check a corrupt PDF gets handed to the appraiser.
			const blob = comparePDF?.data;
			if (
				!(blob instanceof Blob) ||
				blob.size === 0 ||
				blob.type.includes("json")
			) {
				message.error(
					"The report could not be generated. Please try again.",
				);
				console.error("PDF generation failed:", comparePDF);
				return;
			}

			if (reportRef.current?.url) {
				window.URL.revokeObjectURL(reportRef.current.url);
			}
			const nextReport = {
				url: window.URL.createObjectURL(blob),
				filename: buildFileName(inputValues),
			};
			reportRef.current = nextReport;
			setReport(nextReport);

			// The request has to finish before the report exists, which puts this
			// call outside the click's user-gesture window — pop-up blockers stop it
			// routinely, and window.open returns null when they do.
			const preview = window.open(nextReport.url, "_blank");
			if (!preview) {
				message.warning(
					"Your browser blocked the report pop-up. The report is ready — use “Download report” below, or allow pop-ups for this site.",
					8,
				);
			}
		} catch (err) {
			message.error("The report could not be generated. Please try again.");
			console.error("PDF generation error:", err);
		} finally {
			setLoading(false);
		}
	};

	const actions = [
		{
			title: "Update Market Value List",
			description: "Review and update comparable vehicle listings for accurate market valuation",
			onClick: handleMarketList,
			icon: (
				<svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
				</svg>
			),
			gradient: "from-cyan-500 to-blue-600",
			shadowColor: "shadow-cyan-500/20",
			delay: 0,
		},
		{
			title: "Update Salvage Value List",
			description: "Manage salvage vehicle comparables for total loss determination",
			onClick: handleSalvageList,
			icon: (
				<svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
				</svg>
			),
			gradient: "from-violet-500 to-purple-600",
			shadowColor: "shadow-violet-500/20",
			delay: 100,
		},
		{
			title: "Generate Conditions Report",
			description: "Create a professional PDF report with market and salvage analysis",
			onClick: handleGenerate,
			icon: (
				<svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
				</svg>
			),
			gradient: "from-emerald-500 to-teal-600",
			shadowColor: "shadow-emerald-500/20",
			isPrimary: true,
			delay: 200,
		},
		{
			title: "Start New Evaluation",
			description: "Begin a fresh vehicle appraisal from scratch with new details",
			onClick: handleForm,
			icon: (
				<svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
			),
			gradient: "from-sky-500 to-indigo-600",
			shadowColor: "shadow-sky-500/20",
			delay: 300,
		},
	];

	return (
		<Spin
			spinning={loading}
			indicator={
				<LoadingOutlined
					style={{
						fontSize: 48,
						color: "#38bdf8",
					}}
					spin
				/>
			}
		>
			<div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden">
				{/* Background effects */}
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					<div className="absolute top-20 -left-20 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-blob"></div>
					<div className="absolute bottom-20 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
					<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/5 rounded-full blur-3xl"></div>
				</div>

				{/* Grid overlay */}
				<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

				<div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
					{/* Header */}
					<div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
						<h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
							Evaluation{" "}
							<span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
								Dashboard
							</span>
						</h1>
						<p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
							Manage your vehicle appraisal workflow — update comparables, generate reports, or start fresh.
						</p>
					</div>

					{/* Generated report — stays available so a blocked pop-up never
					    loses the report the appraiser just waited for. */}
					{report && (
						<div className="max-w-4xl mx-auto mb-6">
							<div className="rounded-2xl border border-emerald-500/30 bg-emerald-600/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
								<div className="flex items-center gap-3 min-w-0">
									<div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-lg">
										<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
										</svg>
									</div>
									<div className="min-w-0">
										<p className="text-white font-medium text-sm">
											Your conditions report is ready
										</p>
										<p className="text-slate-400 text-xs truncate">
											{report.filename}
										</p>
									</div>
								</div>
								<div className="flex items-center gap-2 shrink-0">
									<button
										type="button"
										onClick={() => triggerDownload(report)}
										className="px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/25 transition-all duration-300"
									>
										Download report
									</button>
									<button
										type="button"
										onClick={() => window.open(report.url, "_blank")}
										className="px-5 py-2.5 rounded-xl font-medium text-sm border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors duration-300"
									>
										Open
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Action Cards Grid */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
						{actions.map((action, index) => (
							<button
								key={index}
								type="button"
								onClick={action.onClick}
								className={`group relative text-left rounded-2xl border transition-all duration-500 ${
									isVisible
										? "opacity-100 translate-y-0"
										: "opacity-0 translate-y-8"
								} ${
									action.isPrimary
										? "bg-gradient-to-br from-emerald-600/20 to-teal-600/10 border-emerald-500/30 hover:border-emerald-400/50"
										: "bg-white/[0.04] border-white/[0.08] hover:border-white/20"
								} hover:bg-white/[0.08] hover:-translate-y-1 hover:shadow-2xl ${action.shadowColor}`}
								style={{ transitionDelay: `${action.delay + 200}ms` }}
							>
								{/* Glow effect on hover */}
								<div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-[0.06] transition-opacity duration-500`}></div>

								<div className="relative p-6 sm:p-7">
									<div className="flex items-start gap-4">
										{/* Icon */}
										<div className={`shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
											{action.icon}
										</div>

										{/* Text */}
										<div className="min-w-0">
											<h3 className="text-lg font-semibold text-white mb-1 group-hover:text-cyan-100 transition-colors duration-300">
												{action.title}
											</h3>
											<p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
												{action.description}
											</p>
										</div>

										{/* Arrow */}
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="shrink-0 w-5 h-5 text-gray-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all duration-300 mt-1"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
										</svg>
									</div>
								</div>
							</button>
						))}
					</div>
				</div>
			</div>
		</Spin>
	);
};

export default Generate;
